import api from "./api";
import { uploadAnexo, Anexo } from "./tarefas";

export interface MotivoAjuste {
  id: string;
  slug: string;
  label: string;
  ativo: boolean;
  ordem: number;
}

export interface TarefaAjuste {
  id: string;
  tarefaId: string;
  categoria: "interno" | "cliente";
  motivoId: string | null;
  descricao: string | null;
  criadoEm: string;
  resolvidoEm: string | null;
  criadoPor?: string | null;
  motivo?: { id: string; label: string; slug: string } | null;
  anexos?: Anexo[];
}

// ===== Motivos (catálogo) =====
export async function listarMotivosAjuste(opts?: { todos?: boolean }) {
  const r = await api.get(`/motivos-ajuste${opts?.todos ? "?todos=1" : ""}`);
  return r.data.data as MotivoAjuste[];
}
export async function criarMotivoAjuste(dados: { label: string; ativo?: boolean; ordem?: number }) {
  const r = await api.post("/motivos-ajuste", dados);
  return r.data.data as MotivoAjuste;
}
export async function atualizarMotivoAjuste(id: string, dados: Partial<{ label: string; ativo: boolean; ordem: number }>) {
  const r = await api.patch(`/motivos-ajuste/${id}`, dados);
  return r.data.data as MotivoAjuste;
}
export async function excluirMotivoAjuste(id: string) {
  await api.delete(`/motivos-ajuste/${id}`);
}

// ===== Ajustes na tarefa =====
export async function listarAjustesTarefa(tarefaId: string) {
  const r = await api.get(`/tarefas/${tarefaId}/ajustes`);
  return r.data.data as TarefaAjuste[];
}
export async function registrarAjuste(
  tarefaId: string,
  dados: { categoria: "interno" | "cliente"; motivoId?: string | null; descricao?: string | null },
) {
  const r = await api.post(`/tarefas/${tarefaId}/ajustes`, dados);
  return r.data.data as TarefaAjuste;
}

/**
 * Cria o ajuste e, em seguida, anexa os arquivos a ELE (foto/vídeo/pdf/áudio).
 * Cada upload é best-effort — se um arquivo falhar, os demais seguem e o ajuste
 * permanece criado.
 */
export async function registrarAjusteComAnexos(
  tarefaId: string,
  dados: { categoria: "interno" | "cliente"; motivoId?: string | null; descricao?: string | null },
  arquivos: File[] = [],
): Promise<{ ajuste: TarefaAjuste; falhas: string[] }> {
  const ajuste = await registrarAjuste(tarefaId, dados);
  const falhas: string[] = [];
  for (const f of arquivos) {
    try {
      await uploadAnexo(tarefaId, f, { ajusteId: ajuste.id });
    } catch {
      // best-effort: não trava o ajuste; coleta o nome pra avisar quem registrou
      falhas.push(f.name || "arquivo");
    }
  }
  return { ajuste, falhas };
}

/** Remove um ajuste (backend só permite se ele não tiver tempo lançado nem estiver resolvido). */
export async function deletarAjuste(tarefaId: string, ajusteId: string) {
  await api.delete(`/tarefas/${tarefaId}/ajustes/${ajusteId}`);
}

/** Marca um ajuste como resolvido (verde) ou reabre. */
export async function resolverAjuste(tarefaId: string, ajusteId: string, resolvido: boolean) {
  const r = await api.patch(`/tarefas/${tarefaId}/ajustes/${ajusteId}/resolver`, { resolvido });
  return r.data.data as TarefaAjuste;
}

/**
 * Edita um ajuste já registrado: texto, motivo e/ou categoria.
 * CAMPO AUSENTE = NÃO MEXE (dá pra trocar só o motivo sem levar o texto junto).
 * Também é por aqui que passa o clique na caixinha de checklist do ajuste.
 * Motivo/categoria só quem registrou ou gestor/admin (o backend recusa o resto).
 */
export async function editarAjuste(
  tarefaId: string,
  ajusteId: string,
  dados: { descricao?: string | null; motivoId?: string | null; categoria?: "interno" | "cliente" },
) {
  const r = await api.patch(`/tarefas/${tarefaId}/ajustes/${ajusteId}`, dados);
  return r.data.data as TarefaAjuste;
}

// ===== Relatório =====
export interface RelatorioAjusteUsuario {
  usuario: { id: string; nome: string; fotoUrl: string | null };
  totalUtilMs: number; // tempo em ajustes (só existe quando alguém deu play)
  /** QUANTIDADE de ajustes no período — número principal do relatório. */
  qtdAjustes: number;
  totalTrabalhadoMs: number; // melhoria 23 — total trabalhado no período
  percentualAjuste: number; // melhoria 23 — ajustes / trabalhado (0..100)
  porMotivo: Array<{
    motivoId: string | null;
    motivoLabel: string;
    categoria: string;
    totalUtilMs: number;
    quantidade: number;
    percentual: number;
  }>;
  /** `qtdAjustes` por tarefa = quantas vezes o card voltou pra ajustes. */
  tarefas: Array<{ tarefaId: string; titulo: string; projetoNome: string | null; totalUtilMs: number; qtdAjustes: number }>;
}
export interface RelatorioAjustes {
  geral: { totalTrabalhadoMs: number; totalAjustesMs: number; percentual: number; qtdAjustes: number };
  usuarios: RelatorioAjusteUsuario[];
}
export async function relatorioAjustes(params: { inicio: string; fim: string; usuarioId?: string }) {
  const qs = new URLSearchParams({ inicio: params.inicio, fim: params.fim });
  if (params.usuarioId) qs.append("usuarioId", params.usuarioId);
  const r = await api.get(`/me/relatorio-ajustes?${qs.toString()}`);
  return r.data.data as RelatorioAjustes;
}
